import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { MonoTypeOperatorFunction, finalize } from 'rxjs';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { ManualInvoiceDialogComponent } from '../dialogs/manual-invoice-dialog/manual-invoice-dialog.component';
import {
  ContractAdminPayload,
  ContractInvoice,
  ContractService,
  INVOICE_APPROVABLE,
  INVOICE_CANCELLABLE,
  lastDayOf,
  openPdfBlob,
} from '../../services/contract.service';

type InvoiceAction = 'approve' | 'discard' | 'cancel' | 'pdf';

/** Eine Rechnungszeile, fertig fuer das Template (Regel 18: einmal je Laden
 *  gebaut, kein Getter, der bei jedem Durchlauf neue Objekte liefert). */
interface InvoiceRow {
  invoice: ContractInvoice;
  title: string;
  periodFrom?: string | null;
  periodTo?: string | null;
  tone: 'neutral' | 'info' | 'ok' | 'warn' | 'error' | 'off';
  canApprove: boolean;
  canDiscard: boolean;
  canCancel: boolean;
}

const TONE: Record<string, InvoiceRow['tone']> = {
  draft: 'neutral',
  approved: 'neutral',
  submitted: 'info',
  issued: 'info',
  sent: 'info',
  paid: 'ok',
  overdue: 'warn',
  dunning: 'error',
  failed: 'error',
  cancelled: 'off',
  discarded: 'off',
};

/**
 * Super-Admin, Reiter „Vertrag“ › Abrechnung (PRD custom-contracts, Phase 3):
 * Hinweise, Vorschau der naechsten Rechnung, Rechnungsliste mit Freigeben,
 * Verwerfen, Stornieren, PDF und manuelle Rechnung. Nach jeder Aktion meldet
 * `changed`, der Editor laedt dann nur den Abrechnungsteil neu.
 */
@Component({
  selector: 'app-contract-invoices',
  templateUrl: './contract-invoices.component.html',
  styleUrl: './contract-invoices.component.scss',
})
export class ContractInvoicesComponent implements OnChanges {
  @Input() clientId!: string;
  @Input() payload?: ContractAdminPayload;
  @Output() changed = new EventEmitter<void>();

  rows: InvoiceRow[] = [];
  missingFields: string[] = [];
  busyId?: string;
  busyAction?: InvoiceAction;

  constructor(
    protected contractService: ContractService,
    protected dialogService: NbDialogService,
    protected toastr: NbToastrService,
    protected t: TranslateService
  ) {}

  ngOnChanges(): void {
    this.missingFields = (this.payload?.billing_identity_missing || []).map((field) =>
      this.t.instant('dialogs.contract.invoices.billingFields.' + field)
    );
    this.rows = (this.payload?.invoices || []).map((invoice) => this.toRow(invoice));
  }

  get contract() {
    return this.payload?.contract ?? null;
  }

  get next() {
    return this.payload?.next_invoice ?? null;
  }

  get nextLastDay(): string | null {
    return lastDayOf(this.next?.service_end || this.next?.usage_end);
  }

  isBusy(row: InvoiceRow, action?: InvoiceAction): boolean {
    return this.busyId === row.invoice.id && (!action || this.busyAction === action);
  }

  trackRow(_: number, row: InvoiceRow): string {
    return row.invoice.id;
  }

  private needsResend(invoice: ContractInvoice): boolean {
    return invoice.status === 'issued' && !invoice.sent_at && !!invoice.last_error;
  }

  private toRow(invoice: ContractInvoice): InvoiceRow {
    const from = invoice.service_start || invoice.usage_start;
    const to = lastDayOf(invoice.service_end || invoice.usage_end);
    return {
      invoice,
      title:
        invoice.number ||
        this.t.instant(
          invoice.kind === 'manual'
            ? 'dialogs.contract.invoices.manualDraft'
            : 'dialogs.contract.invoices.periodDraft'
        ),
      periodFrom: from,
      periodTo: to,
      tone: TONE[invoice.status] || 'neutral',
      // Ausgestellt, aber die Mail ging nicht raus (last_error): Freigabe sendet nur erneut.
      canApprove: INVOICE_APPROVABLE.includes(invoice.status) || this.needsResend(invoice),
      canDiscard: INVOICE_APPROVABLE.includes(invoice.status),
      canCancel: INVOICE_CANCELLABLE.includes(invoice.status),
    };
  }

  private run<T>(row: InvoiceRow, action: InvoiceAction): MonoTypeOperatorFunction<T> {
    this.busyId = row.invoice.id;
    this.busyAction = action;
    return finalize<T>(() => {
      this.busyId = undefined;
      this.busyAction = undefined;
    });
  }

  private money(cents: number): string {
    return (cents / 100).toLocaleString(this.t.currentLang === 'en' ? 'en-GB' : 'de-DE', {
      style: 'currency',
      currency: 'EUR',
    });
  }

  // ------------------------------------------------------------------ actions

  approve(row: InvoiceRow): void {
    if (this.busyId) return;
    const recipient = this.contract?.invoice_email || this.t.instant('dialogs.contract.invoices.defaultRecipient');
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.contract.invoices.approve.title',
          message: this.t.instant('dialogs.contract.invoices.approve.message', {
            amount: this.money(row.invoice.net_cents),
            recipient,
          }),
          confirmLabel: 'dialogs.contract.invoices.actions.approve',
        },
      })
      .onClose.subscribe((result: any) => {
        if (!result?.confirmed) return;
        this.contractService
          .approveInvoice(row.invoice.id)
          .pipe(this.run(row, 'approve'))
          .subscribe({
            next: (invoice) => {
              this.toastr.success(
                this.t.instant('dialogs.contract.invoices.toasts.approved', {
                  number: invoice.number || '',
                }),
                this.t.instant('toasts.success')
              );
              this.changed.emit();
            },
            error: (err: HttpErrorResponse) => {
              this.toastr.danger(this.approveError(err), this.t.instant('dialogs.contract.invoices.errors.title'), {
                duration: 8000,
              });
              // Die Rechnung steht jetzt auf „fehlgeschlagen“ mit Fehlertext.
              this.changed.emit();
            },
          });
      });
  }

  /** 503 nicht angebunden, 422 Angaben fehlen, 502 alphacount — jeweils mit
   *  dem Text des Backends. */
  private approveError(err: HttpErrorResponse): string {
    const detail = err?.error?.detail;
    const text = typeof detail === 'string' ? detail : detail?.message || '';
    const key =
      err.status === 503
        ? 'notConfigured'
        : err.status === 422
          ? 'incomplete'
          : err.status === 502
            ? 'alphacount'
            : 'generic';
    return this.t.instant('dialogs.contract.invoices.errors.' + key, { detail: text });
  }

  discard(row: InvoiceRow): void {
    if (this.busyId) return;
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.contract.invoices.discard.title',
          message: this.t.instant('dialogs.contract.invoices.discard.message', { title: row.title }),
          confirmLabel: 'dialogs.contract.invoices.actions.discard',
          inputLabel: 'dialogs.contract.invoices.discard.noteLabel',
          inputPlaceholder: 'dialogs.contract.invoices.discard.noteLabel',
          inputMaxLength: 2000,
        },
      })
      .onClose.subscribe((result: any) => {
        if (!result?.confirmed) return;
        this.contractService
          .discardInvoice(row.invoice.id, (result.value || '').trim() || null)
          .pipe(this.run(row, 'discard'))
          .subscribe({
            next: () => {
              this.toastr.success(
                this.t.instant('dialogs.contract.invoices.toasts.discarded'),
                this.t.instant('toasts.success')
              );
              this.changed.emit();
            },
            error: () => undefined,
          });
      });
  }

  cancel(row: InvoiceRow): void {
    if (this.busyId) return;
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.contract.invoices.cancel.title',
          message: this.t.instant('dialogs.contract.invoices.cancel.message', { number: row.title }),
          confirmLabel: 'dialogs.contract.invoices.actions.cancel',
        },
      })
      .onClose.subscribe((result: any) => {
        if (!result?.confirmed) return;
        this.contractService
          .cancelInvoice(row.invoice.id)
          .pipe(this.run(row, 'cancel'))
          .subscribe({
            next: () => {
              this.toastr.success(
                this.t.instant('dialogs.contract.invoices.toasts.cancelled'),
                this.t.instant('toasts.success')
              );
              this.changed.emit();
            },
            error: () => undefined,
          });
      });
  }

  pdf(row: InvoiceRow): void {
    if (this.busyId) return;
    this.contractService
      .getAdminInvoicePdf(row.invoice.id)
      .pipe(this.run(row, 'pdf'))
      .subscribe({
        next: (blob) => openPdfBlob(blob, `${row.invoice.number || row.invoice.id}.pdf`),
        error: () => undefined,
      });
  }

  createManual(): void {
    this.dialogService
      .open(ManualInvoiceDialogComponent, {
        context: {
          clientId: this.clientId,
          paymentTermsDays: this.contract?.payment_terms_days ?? 14,
        },
      })
      .onClose.subscribe((invoice?: ContractInvoice) => {
        if (!invoice) return;
        this.toastr.success(
          this.t.instant('dialogs.contract.invoices.toasts.created'),
          this.t.instant('toasts.success')
        );
        this.changed.emit();
      });
  }
}
